import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ALL_PRODUCTS } from "../data/products";
import useReveal from "../hooks/useReveal";
import "./ProductDetail.css";

export default function ProductDetail() {

  const { id } = useParams();
  const product = ALL_PRODUCTS.find((p) => p.id === id);

  const [activeImg, setActiveImg] = useState(0);
  const [selectedWeight, setSelectedWeight] = useState(null);

  useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
    // Set first weight variant as default
    if (product?.weight && Array.isArray(product.weight)) {
      setSelectedWeight(product.weight[0]);
    } else {
      setSelectedWeight(product?.weight || null);
    }
  }, [id]);

  if (!product) {
    return (
      <main style={{ paddingTop: "70px", textAlign: "center", padding: "8rem 2rem" }}>
        <p style={{ fontSize: "3rem" }}>🔍</p>
        <h2 style={{ fontFamily: "var(--font-display)", color: "var(--navy)", marginTop: "1rem" }}>
          Product Not Found
        </h2>
        <Link
          to="/products"
          className="btn btn-primary"
          style={{ marginTop: "2rem", display: "inline-flex" }}
        >
          ← All Products
        </Link>
      </main>
    );
  }

  // Build images array: supports both imgs[] array and img1/img2/img3 individual keys
  const buildImagesArray = (p) => {
    if (p.imgs && p.imgs.length > 0) return p.imgs;
    const collected = [];
    if (p.img1) collected.push(p.img1);
    if (p.img2) collected.push(p.img2);
    if (p.img3) collected.push(p.img3);
    if (p.img4) collected.push(p.img4);
    if (collected.length > 0) return collected;
    if (p.img) return [p.img];
    return ["/images/no-image.webp"];
  };

  const images = buildImagesArray(product);

  // Weight variants
  const weightVariants = Array.isArray(product.weight)
    ? product.weight
    : product.weight
    ? [product.weight]
    : [];

  // Packing/pricing for selected weight
  const packingPrice =
    product.packing && selectedWeight
      ? product.packing[selectedWeight] || null
      : null;

  const related = ALL_PRODUCTS
    .filter((p) => p.type === product.type && p.id !== product.id)
    .slice(0, 3);

  return (
    <main style={{ paddingTop: "70px" }}>

      {/* BREADCRUMB */}
      <nav className="breadcrumb">
        <div className="container breadcrumb__inner">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/products">Products</Link>
          <span>/</span>
          <span>{product.name}</span>
        </div>
      </nav>

      {/* PRODUCT SECTION */}
      <section className="section pd-section">
        <div className="container">
          <div className="pd-grid">

            {/* IMAGES */}
            <div className="pd-images reveal-l">

              <div className="pd-main-img">
                <img
                  src={images[activeImg]}
                  alt={product.name}
                />
                <div className="pd-tier-tag">{product.tier}</div>
              </div>

              {images.length > 1 && (
                <div className="pd-thumbs">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      className={`pd-thumb ${i === activeImg ? "pd-thumb--active" : ""}`}
                      onClick={() => setActiveImg(i)}
                    >
                      <img src={img} alt={`${product.name} view ${i + 1}`} />
                    </button>
                  ))}
                </div>
              )}

            </div>

            {/* DETAILS */}
            <div className="pd-details reveal-r">

              <div className="eyebrow">
                {product.type} • {product.fragrance}
              </div>

              <h1 className="h-display">{product.name}</h1>

              <p className="h-sub">{product.longDesc || product.desc}</p>

              {/* WEIGHT VARIANTS */}
              {weightVariants.length > 0 && (
                <div className="pd-weight-section">
                  <p className="pd-weight-label">Select Size</p>
                  <div className="pd-weight-options">
                    {weightVariants.map((w) => (
                      <button
                        key={w}
                        className={`pd-weight-btn ${selectedWeight === w ? "pd-weight-btn--active" : ""}`}
                        onClick={() => setSelectedWeight(w)}
                      >
                        {w}
                      </button>
                    ))}
                  </div>
                  {packingPrice && (
                    <p className="pd-mrp">{packingPrice}</p>
                  )}
                </div>
              )}

              {/* SPECS */}
              <div className="pd-specs">

                {[
                  ["⚖️", "Weight", weightVariants.length > 0 ? weightVariants.join(" / ") : product.weight],
                  ["⏱", "Burn Time", product.burnTime],
                  ["🌸", "Fragrance", product.fragrance],
                  ...(product.material ? [["🪵", "Material", product.material]] : []),
                ].filter(([,, val]) => val).map(([icon, label, val]) => (
                  <div key={label} className="pd-spec">
                    <span>{icon}</span>
                    <div>
                      <strong>{label}</strong>
                      <p>{val}</p>
                    </div>
                  </div>
                ))}

              </div>

              {/* HIGHLIGHTS */}
              {product.highlights && product.highlights.length > 0 && (
                <div className="pd-highlights">
                  {product.highlights.map((h, i) => (
                    <span key={i} className="pd-hl">✓ {h}</span>
                  ))}
                </div>
              )}

              {/* BENEFITS */}
              {product.benefits && product.benefits.length > 0 && (
                <div className="pd-benefits">
                  <p className="pd-benefits-label">Key Benefits</p>
                  <ul className="pd-benefits-list">
                    {product.benefits.map((b, i) => (
                      <li key={i}>🌿 {b}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* PACKING INFO */}
              {product.packing && (
                <div className="pd-packing">
                  <p className="pd-packing-label">Packing Options</p>
                  <div className="pd-packing-grid">
                    {Object.entries(product.packing).map(([size, price]) => (
                      <div key={size} className={`pd-packing-item ${selectedWeight === size ? "pd-packing-item--active" : ""}`}>
                        <span className="pd-packing-size">{size}</span>
                        <span className="pd-packing-price">{price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ACTIONS */}
              <div className="pd-actions">
                <a
                  href={product.flipkartUrl || "https://www.flipkart.com"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-flipkart"
                >
                  🛒 Buy on Flipkart
                </a>

                <a
                  href={`https://wa.me/919320905751?text=${encodeURIComponent(
                    `Hi, I want to enquire about ${product.name}${selectedWeight ? ` (${selectedWeight})` : ""}`
                  )}`}
                  className="btn btn-wa"
                  target="_blank"
                  rel="noreferrer"
                >
                  Get Enquiry on WhatsApp
                </a>
              </div>

              <div className="pd-trust">
                <span>🚚 Pan-India Delivery</span>
                <span>🌿 Natural Ingredients</span>
                <span>✋ Handcrafted</span>
                <span>📍 Made in Mumbai</span>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* PRODUCT INFO + HOW TO USE */}
      <section className="section pd-info-section">
        <div className="container">
          <div className="pd-info-grid">

            {/* LEFT: PRODUCT INFORMATION TABLE */}
            <div className="pd-info-card reveal-l">
              <h3>🌿 Product Information</h3>
              <table className="pd-table">
                <tbody>
                  {[
                    ["Brand",       product.brand       || "Prakhyat Aroma Products"],
                    ["Type",        product.type],
                    ["Tier",        product.tier],
                    ["Fragrance",   product.fragrance],
                    ["Weight",      Array.isArray(product.weight) ? product.weight.join(", ") : product.weight],
                    ["Material",    product.material],
                    ["Contents",    product.contents],
                    ["Burn Time",   product.burnTime],
                    ["Origin",      product.origin       || "Made in Virar, Palghar, India"],
                    ["Ingredients", product.ingredients],
                  ].filter(([, val]) => val).map(([label, val]) => (
                    <tr key={label}>
                      <td className="pd-th">{label}</td>
                      <td>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* RIGHT: HOW TO USE */}
            <div className="pd-info-card reveal-r">
              <h3>🔥 How to Use</h3>
              <ol className="pd-steps">
                {(product.howToUse || [
                  "Place agarbatti / dhoop in a suitable holder or stand.",
                  "Light the tip and allow to flame for 5–10 seconds.",
                  "Gently blow out — the tip should glow and emit smoke.",
                  "Place in a safe, ventilated area away from flammables.",
                  "Let the sacred fragrance fill your space.",
                  "Never leave burning incense unattended.",
                ]).map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>

              <div className="pd-safety">
                <h4>⚠ Safety</h4>
                <p>
                  {product.safety ||
                    "Keep away from children and pets. Use in ventilated spaces. Store in cool, dry place."}
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* RELATED PRODUCTS */}
      {related.length > 0 && (
        <section className="section">
          <div className="container">

            <div className="text-center reveal" style={{ marginBottom: "2.5rem" }}>
              <h2 className="h-display">Related Products</h2>
            </div>

            <div className="related-grid">
              {related.map((r) => {
                const rImgs = buildImagesArray(r);
                return (
                  <div key={r.id} className="p-card">

                    <Link to={`/products/${r.id}`}>
                      <div className="p-card-img">
                        <img
                          src={rImgs[0]}
                          alt={r.name}
                          loading="lazy"
                        />
                        <div className="p-card-tier">{r.tier}</div>
                      </div>
                    </Link>

                    <div className="p-card-body">
                      <p className="p-card-frag">✦ {r.fragrance}</p>
                      <h3 className="p-card-name">{r.name}</h3>
                      <p className="p-card-desc">{r.shortDesc || r.desc}</p>

                      <div className="p-card-info">
                        <span>⏱ {r.burnTime}</span>
                        <span>⚖️ {Array.isArray(r.weight) ? r.weight.join(" / ") : r.weight}</span>
                      </div>

                      <div className="p-card-actions">
                        <Link to={`/products/${r.id}`} className="btn-card-view">
                          View Details
                        </Link>
                        <a
                          href={r.flipkartUrl || "https://www.flipkart.com"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-card-flipkart"
                          onClick={(e) => e.stopPropagation()}
                        >
                          🛒 Buy
                        </a>
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>

          </div>
        </section>
      )}

    </main>
  );
}
